import {Link as PrimerLink} from '@primer/react'
import {Link} from '@github-ui/react-core/link'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {safeHref} from '../utils'

interface SidebarLinkProps {
  /** Internal route path (renders as client-side Link). */
  to?: string
  /** External or anchor URL (renders as plain href). */
  href?: string
  /** Analytics label suffix, e.g. 'section:stars' or 'file:readme'. Automatically prefixed with 'location:sidebar;'. */
  analyticsLabel?: string
  /** When true, forces a full page navigation instead of a soft navigation. */
  reloadDocument?: boolean
  muted?: boolean
  inline?: boolean
  className?: string
  /** Link target attribute (e.g. '_blank' for new tab). */
  target?: string
  /** Link rel attribute (e.g. 'noopener noreferrer' for external links). */
  rel?: string
  /** Optional click handler, called in addition to analytics tracking. */
  onClick?: () => void
  children: React.ReactNode
  /** Additional HTML attributes forwarded to the underlying anchor (e.g. hovercard data-* attributes). */
  [key: `data-${string}`]: string | undefined
}

/** A link that automatically tracks clicks with sidebar analytics. */
export function SidebarLink({
  to,
  href,
  analyticsLabel,
  reloadDocument,
  muted,
  inline,
  className,
  target,
  rel,
  onClick,
  children,
  ...rest
}: SidebarLinkProps) {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const handleClick = () => {
    if (analyticsLabel) {
      sendClickAnalyticsEvent({
        category: 'REPOSITORY_OVERVIEW',
        action: 'click',
        label: `location:sidebar;${analyticsLabel}`,
      })
    }
    onClick?.()
  }

  if (to) {
    return (
      <PrimerLink
        as={Link}
        to={to}
        reloadDocument={reloadDocument}
        muted={muted}
        inline={inline}
        className={className}
        onClick={handleClick}
        {...rest}
      >
        {children}
      </PrimerLink>
    )
  }

  return (
    <PrimerLink
      href={safeHref(href)}
      target={target}
      rel={rel}
      muted={muted}
      inline={inline}
      className={className}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </PrimerLink>
  )
}
